import { useCallback, useEffect, useState, useRef } from 'react';
import { useLayoutEffect, useAppSelector, useAppDispatch } from '~app/hooks';
import { selectActive, selectViews, setActive } from '~app/components/Survey/SurveyFormat';
import { isAnimating, scrollIntoView } from './utils/scrollIntoView';

/** Whether a `mousedown` event has occurred within 200ms, exported to be used in OQAAT focus handling */
let clicking = false;

export default function ActiveViewScrollManager(): null {
  const active = useAppSelector(selectActive);
  const views = useAppSelector(selectViews);
  const dispatch = useAppDispatch();

  const [viewElements, setViewElements] = useState<HTMLElement[]>([]);

  useEffect(() => {
    const elements: HTMLElement[] = [];
    views.forEach(view => {
      const el = document.getElementById(`view-${view}`);
      if (el) {
        elements.push(el);
      }
    });
    setViewElements(elements);
  }, [views]);

  const findActiveAnchor = useCallback(() => {
    if (isAnimating()) {
      return;
    }
    let newIdx = -1;
    if (window.scrollY === 0) {
      newIdx = 0;
    } else {
      const winMidpoint = window.innerHeight / 2;
      viewElements.forEach((el, idx) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= winMidpoint) {
          newIdx = idx;
        }
      });
    }

    const currentAnchor = views[newIdx];

    if (currentAnchor && currentAnchor !== active) {
      dispatch(setActive(currentAnchor));
    }
  }, [views, active, viewElements, dispatch]);

  /**
   * Set `clicking` variable on click, which is used to determine OQAAT scroll behaviour
   * for focus events.
   * note: listening to `mousedown` events as they trigger before `focus` events
   */
  const handleClick = useCallback(e => {
    clicking = true;
    setTimeout(() => {
      clicking = false;
    }, 200);
  }, []);

  /**
   * Scroll to currently focused input on keydown if question is inactive
   * (makes it less confusing for the user when they've scrolled away from focused input)
   */
  const handleKeydown = useCallback(
    e => {
      /** The keys which will trigger the scroll animation - matches all alphanumeric & non-word characters */
      const keysToMatch = /^([a-z0-9]|\W|_)$/i;
      const focusedId = document.activeElement?.id;
      if (
        keysToMatch.test(e.key) && // if the key pressed is a letter or non-word character
        focusedId && // if there is a focused element
        document.activeElement?.tagName === 'INPUT' && // if the focused element is an input
        views.includes(focusedId) && // if the focused element is inside a view
        focusedId !== active // if the focused view is not active
      ) {
        scrollIntoView(document.activeElement as HTMLElement);
        // activate the question being scrolled to (scroll listener is disabled during animation)
        if (views.includes(document.activeElement.id)) {
          dispatch(setActive(document.activeElement.id));
        }
      }
    },
    [dispatch, views, active]
  );

  useLayoutEffect(() => {
    window.addEventListener('scroll', findActiveAnchor);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('scroll', findActiveAnchor);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [findActiveAnchor, handleClick, handleKeydown]);

  // Initially focus on first active view
  // Only run on first render (prevents active state being stuck on top question when scrolled up on larger screens)
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      findActiveAnchor();
      if (viewElements.length) {
        firstUpdate.current = false;
      }
    }
  }, [viewElements, findActiveAnchor]);

  return null;
}

/** Whether a `mousedown` event has occurred within the last 200ms */
export const isClicking = (): boolean => clicking;
